import type { ReactNode } from "react";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { Label as ShadcnLabel } from "../shadcn/label";
import { Switch as ShadcnSwitch, type SwitchSize } from "../shadcn/switch";

export type SwitchProps = {
  id?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  ariaLabel?: string;
  labelClassName?: string;
  className?: string;
  containerClassName?: string;
  isLoading?: boolean;
  name?: string;
  labelPosition?: "left" | "right";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  dataTestId?: string;
  size?: SwitchSize;
};

export const Switch = ({
  id,
  name,
  checked,
  onChange,
  disabled,
  label,
  ariaLabel,
  labelClassName,
  className,
  containerClassName,
  isLoading,
  labelPosition = "right",
  leftIcon,
  rightIcon,
  dataTestId,
  size,
}: SwitchProps) => {
  const inputId = id ?? `switch-${label}`;
  const testId = dataTestId ?? inputId;

  const labelElement = isTruthy(label) ? (
    <ShadcnLabel className={labelClassName} htmlFor={inputId}>
      {label}
    </ShadcnLabel>
  ) : null;

  const iconWrapperClassName = "inline-flex items-center";

  return (
    <div className={cnTwMerge("flex items-center gap-2", containerClassName)}>
      {labelPosition === "left" ? labelElement : null}
      {isTruthy(leftIcon) ? (
        <span aria-hidden="true" className={iconWrapperClassName}>
          {leftIcon}
        </span>
      ) : null}
      <ShadcnSwitch
        aria-label={ariaLabel}
        checked={checked}
        className={cnTwMerge(
          "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-default",
          className,
        )}
        data-testid={testId}
        disabled={disabled || isLoading}
        id={inputId}
        isLoading={isLoading}
        name={name}
        onCheckedChange={onChange}
        size={size}
      />
      {isTruthy(rightIcon) ? (
        <span aria-hidden="true" className={iconWrapperClassName}>
          {rightIcon}
        </span>
      ) : null}
      {labelPosition === "right" ? labelElement : null}
    </div>
  );
};
